import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { alias } from './utils';
import { markPageRefresh } from './pageRefreshMarker';

const POLLING_INTERVAL =
  parseFloat(process.env.REACT_APP_POLLING_INTERVAL || "5") * 60 * 1000; // 5 minutes

export const VersionPoller = () => {
  const currentVersion = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const basePath = alias ? (alias.startsWith('/') ? alias : `/${alias}`) : '';
        const fetchUrl = `${basePath}/meta.json?t=${new Date().getTime()}`;
        console.log(`[VersionPoller] Checking for new version at ${fetchUrl}...`);
        
        const response = await fetch(fetchUrl, {
          cache: 'no-cache',
        });
        
        if (!response.ok) {
          console.log(`[VersionPoller] Fetch failed with status: ${response.status}`);
          return;
        }

        const contentType = response.headers.get("content-type");
        console.log(`[VersionPoller] Server returned Content-Type:`, contentType);
        
        if (!contentType || !contentType.includes("application/json")) {
          console.log(`[VersionPoller] Ignored response because it is not JSON (likely index.html fallback).`);
          return; 
        }

        const data = await response.json();
        console.log(`[VersionPoller] Parsed meta.json:`, data);
        
        if (!data || !data.version) {
          console.log(`[VersionPoller] No version field found in meta.json.`);
          return;
        }

        if (currentVersion.current === null) {
          console.log(`[VersionPoller] Initial load. Setting current version to:`, data.version);
          currentVersion.current = data.version;
        } else if (currentVersion.current !== data.version) {
          console.log(`[VersionPoller] 🚨 New version detected! Waiting for backend before reloading... (${currentVersion.current} -> ${data.version})`);
          
          // Clear any chunk error tracking to allow fresh reload
          sessionStorage.removeItem("chunk-reload");
          
          // Update the current version so subsequent checks don't spawn duplicate reload loops
          currentVersion.current = data.version;
          
          const backendPingUrl = `${process.env.REACT_APP_API_URL || ""}/health`;
          
          const checkBackendAndReload = async () => {
            try {
              const pingResponse = await fetch(backendPingUrl, { method: "GET" });
              // If we get a response (e.g. 200, 401, 403, 444), the backend is up.
              // 502/503/504 means the reverse proxy (Nginx) is up but backend is offline/restarting.
              if (pingResponse.status !== 502 && pingResponse.status !== 503 && pingResponse.status !== 504) {
                console.log(`[VersionPoller] Backend is up (status ${pingResponse.status}). Reloading now!`);
                // Swapping in a new build refreshes the DOCUMENT only — the process
                // survives, so device tracking must not report a close + open for it.
                markPageRefresh();
                window.location.reload();
              } else {
                console.log(`[VersionPoller] Backend not fully ready (status ${pingResponse.status}). Retrying in 3s...`);
                setTimeout(checkBackendAndReload, 3000);
              }
            } catch (err) {
              // Network error (Failed to fetch)
              console.log(`[VersionPoller] Backend still unreachable. Retrying in 3s...`, err);
              setTimeout(checkBackendAndReload, 3000);
            }
          };

          checkBackendAndReload();
        } else {
          console.log(`[VersionPoller] ✅ Version is unchanged (${currentVersion.current}). No reload needed.`);
        }
      } catch (error) {
        console.error('[VersionPoller] Failed to fetch version meta.json:', error);
      }
    };

    // Initial check and check on navigation
    fetchVersion();

    // Poll every 1 minutes as a fallback
    const interval = setInterval(fetchVersion, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [location.pathname]);

  return null;
};
